import axios from 'axios';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { auth } from './firebase';
import type { InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
});

function waitForUser(timeoutMs = 5000): Promise<User | null> {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve) => {
    const to = setTimeout(() => {
      unsub();
      resolve(null);
    }, timeoutMs);
    const unsub = onAuthStateChanged(auth, (u) => {
      clearTimeout(to);
      unsub();
      resolve(u);
    });
  });
}

type AuthedConfig = InternalAxiosRequestConfig & {
  _forceRefresh?: boolean;
  _retried?: boolean;
  auth?: boolean;
};

api.interceptors.request.use(async (cfg) => {
  const config = (cfg || {}) as AuthedConfig;

  config.headers = (config.headers ?? {}) as AxiosRequestHeaders;

  const needsAuth = config.auth !== false;
  if (!needsAuth) return config;

  const user = auth.currentUser ?? (await waitForUser());
  if (!user) {
    if ('Authorization' in config.headers) {
      delete (config.headers as any).Authorization;
    }
    return config;
  }
  const force = config._forceRefresh === true;

  try {
    const token = await user.getIdToken(force);
    (config.headers as any).Authorization = `Bearer ${token}`;
  } catch {
    if ('Authorization' in config.headers) {
      delete (config.headers as any).Authorization;
    }
  }

  return config;
});
let isLoggingOut = false;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.error;
    const original = error.config || {};
    const user = auth.currentUser;

    const retryOnce = async () => {
      if (!user || original._retried) return null;
      try {
        original._retried = true;
        original._forceRefresh = true;
        return await api(original);
      } catch {
        return null;
      }
    };

    const logoutAndRedirect = async (reason: string) => {
      if (isLoggingOut) return;
      isLoggingOut = true;
      try {
        await signOut(auth);
      } catch {}
      const onLogin = window.location.pathname.startsWith('/login');
      if (!onLogin) window.location.assign(`/login?reason=${encodeURIComponent(reason)}`);
    };

    if (status === 401) {
      switch (code) {
        case 'MISSING_TOKEN': {
          const res = await retryOnce();
          if (res) return res;
          await logoutAndRedirect('missing_token');
          return;
        }
        case 'INVALID_TOKEN': {
          const res = await retryOnce();
          if (res) return res;
          await logoutAndRedirect('invalid_token');
          return;
        }
        case 'SESSION_TOO_OLD': {
          await logoutAndRedirect('session_expired');
          return;
        }
        default: {
          const res = await retryOnce();
          if (res) return res;
          await logoutAndRedirect('unauthorized');
          return;
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
